import React from 'react';
import { fmtPercent, overflowMessage } from '../../utils/tds';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TONES = {
  red:   { bg: '#fee2e2', border: '#fca5a5', color: '#b91c1c' },
  amber: { bg: '#fef3c7', border: '#fcd34d', color: '#92400e' },
  blue:  { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
};

const Note = ({ tone, children }) => {
  const t = TONES[tone];
  return (
    <div style={{
      marginTop: 8, padding: '8px 10px', background: t.bg, border: `1px solid ${t.border}`,
      borderRadius: 8, fontSize: 12, lineHeight: 1.5, color: t.color,
    }}>
      {children}
    </div>
  );
};

export const EMPTY_TDS = { edited: null, percent: '', amount: '', section: '', perBill: false, billTds: {} };

/**
 * TdsFields — optional "TDS deducted by customer" block used on every receipt form.
 *
 * % and amount are linked and both editable:
 *   • type a %      → amount = % × taxable base
 *   • type an amount → % = amount ÷ taxable base
 * The field touched last is what you typed; the other is calculated.
 *
 * Messages shown under the fields (all come from resolveTds()):
 *   • error     TDS cannot be saved as entered (e.g. TDS-only on a bill with no payment yet)
 *               (also when the TDS typed is more than can be applied — the box keeps what was typed)
 *   • overflow  payment + TDS is more than the bills need (only when overflowMode is given)
 *   • leftover  a bill still owes money after this receipt although TDS was entered
 *
 * Props
 *   input         { edited:'percent'|'amount'|null, percent, amount, section, perBill?, billTds? }
 *   onChange      (nextInput) => void
 *   resolved      result of resolveTds()
 *   disabled      boolean
 *   onShowPerBill optional () => void — shows an "Enter TDS per bill" link (reveals the TDS column of the allocation table)
 *   overflowMode  'block' | 'wallet' | undefined — how to word the "more than the balance" note.
 *                 Leave undefined when the screen shows that message itself.
 */
const TdsFields = ({ input, onChange, resolved, disabled = false, overflowMode, onShowPerBill }) => {
  const inp = input || EMPTY_TDS;
  const perBill = !!inp.perBill;

  const percentValue = perBill
    ? fmtPercent(resolved?.percent)
    : (inp.edited === 'percent' ? inp.percent : fmtPercent(resolved?.percent));

  // what the user typed stays in the box — a value that cannot be applied is flagged, never trimmed
  const amountValue = perBill
    ? (resolved?.amount > 0 ? String(resolved.amount) : '')
    : inp.edited === 'amount'
      ? inp.amount
      : (resolved?.amount > 0 ? String(resolved.amount) : '');

  const fieldsOff = disabled;

  const labelSt = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
  const inputSt = (off) => ({
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none', background: off ? '#f9fafb' : '#fff',
  });

  const overflowText = overflowMode ? overflowMessage(resolved, overflowMode) : '';
  const leftover = resolved?.leftover || [];

  return (
    <div style={{ padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
        TDS deducted by customer <span style={{ fontWeight: 500, color: '#b45309' }}>(optional)</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 10 }}>
        <div>
          <label style={labelSt}>TDS %</label>
          <input
            type="number" min="0" max="100" step="any" disabled={fieldsOff}
            value={percentValue}
            onChange={(e) => onChange({ ...inp, perBill: false, billTds: {}, edited: 'percent', percent: e.target.value, amount: '' })}
            style={inputSt(fieldsOff)} placeholder="0"
          />
        </div>
        <div>
          <label style={labelSt}>TDS Amount (₹)</label>
          <input
            type="number" min="0" step="0.01" disabled={fieldsOff}
            value={amountValue}
            onChange={(e) => onChange({ ...inp, perBill: false, billTds: {}, edited: 'amount', amount: e.target.value, percent: '' })}
            style={inputSt(fieldsOff)} placeholder="0.00"
          />
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <label style={labelSt}>Section (optional)</label>
        <input
          type="text" maxLength={50} disabled={disabled}
          value={inp.section || ''}
          onChange={(e) => onChange({ ...inp, section: e.target.value })}
          style={inputSt(disabled)} placeholder="e.g. 194C"
        />
      </div>

      <div style={{ marginTop: 8, fontSize: 11.5, color: '#92400e', lineHeight: 1.45 }}>
        {perBill
          ? <>TDS is being entered bill by bill (table on the right) — the total and % here are calculated. Typing a % or amount here replaces the per-bill values.</>
          : resolved?.base > 0
            ? <>Calculated on <strong>{money(resolved.base)}</strong> taxable value (before GST) of the part settled in this receipt. Change % or amount — the other updates.</>
            : <>Calculated on the taxable value (before GST) of the part settled in this receipt. Enter % or amount — the other updates.</>}
        {' '}TDS settles the bill but is <strong>not</strong> bank money.
      </div>

      {onShowPerBill && !disabled && (
        <button
          type="button" onClick={onShowPerBill}
          style={{ marginTop: 6, padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#b45309', textDecoration: 'underline' }}
        >
          Enter TDS per bill
        </button>
      )}

      {resolved?.error && <Note tone="red">{resolved.error}</Note>}

      {!resolved?.error && overflowText && (
        <Note tone={overflowMode === 'block' ? 'red' : 'amber'}>{overflowText}</Note>
      )}

      {!resolved?.error && leftover.length > 0 && (
        <Note tone="blue">
          {leftover.map((l) => (
            <div key={l.billNumber}>
              After this receipt <strong>{l.billNumber}</strong> will still show <strong>{money(l.left)}</strong> due.
            </div>
          ))}
          <div style={{ marginTop: 2 }}>
            TDS is worked out only on the part settled in this receipt — earlier payments that had no TDS are not
            included. To clear the balance, change the TDS amount or the payment.
          </div>
        </Note>
      )}
    </div>
  );
};

export default TdsFields;